import { Document } from '@consigli/types';
import { useCallback, useMemo } from 'react';

import { sortDocumentsByType } from '@/util/document-ordering';

import { ViewerMode, useViewerContext } from './use-viewer-context';

export const useViewer = () => {
  const {
    document: currentDocument,
    setDocument,
    setMode,
    availableDocuments,
    setAvailableDocuments,
    setIsStandaloneDocument,
  } = useViewerContext();

  const orderedDocuments = useMemo(() => {
    return sortDocumentsByType(availableDocuments);
  }, [availableDocuments]);

  const openDocumentViewer = useCallback(
    (doc: Document, mode: ViewerMode, documents?: Document[]) => {
      setDocument(doc);
      setMode(mode);
      setIsStandaloneDocument(false);

      if (documents && documents.length > 0) {
        setAvailableDocuments(documents);
      }
    },
    [setDocument, setMode, setAvailableDocuments, setIsStandaloneDocument],
  );

  /**
   * Opens a document on its own, with no surrounding list or preview behind it —
   * an Agent Chat citation, for instance. The viewer drops the affordances that
   * only make sense within a list: there is nothing to step through and no
   * preview to return to, so only Close is offered.
   */
  const openStandaloneDocument = useCallback(
    (doc: Document) => {
      setDocument(doc);
      setMode(ViewerMode.DocumentFullscreen);
      setIsStandaloneDocument(true);
    },
    [setDocument, setMode, setIsStandaloneDocument],
  );

  const navigateToDocument = useCallback(
    (direction: 'next' | 'previous') => {
      if (!currentDocument) return;

      const docs = orderedDocuments;
      const currentIndex = docs.findIndex((doc) => doc.id === currentDocument.id);

      if (currentIndex === -1 || docs.length <= 1) return;

      let newIndex: number;
      if (direction === 'next') {
        newIndex = (currentIndex + 1) % docs.length;
      } else {
        newIndex = (currentIndex - 1 + docs.length) % docs.length;
      }

      const newDocument = docs[newIndex];
      setDocument(newDocument);
    },
    [currentDocument, orderedDocuments, setDocument],
  );

  const close = useCallback(() => {
    setDocument(null);
    setMode(ViewerMode.Closed);
    setIsStandaloneDocument(false);
  }, [setDocument, setMode, setIsStandaloneDocument]);

  const canNavigatePrevious = useCallback(() => {
    if (!currentDocument) return false;

    const docs = orderedDocuments;
    return docs.length > 1 && docs.findIndex((doc) => doc.id === currentDocument.id) > 0;
  }, [currentDocument, orderedDocuments]);

  const canNavigateNext = useCallback(() => {
    if (!currentDocument) return false;

    const docs = orderedDocuments;
    const currentIndex = docs.findIndex((doc) => doc.id === currentDocument.id);
    return docs.length > 1 && currentIndex !== -1 && currentIndex < docs.length - 1;
  }, [currentDocument, orderedDocuments]);

  return {
    openDocumentViewer,
    openStandaloneDocument,
    close,
    navigateToDocument,
    canNavigatePrevious,
    canNavigateNext,
    availableDocuments: orderedDocuments,
  };
};
